import { DatePicker, Flex, InputNumber, Row } from "antd"
import { DateFormat, FLagWithText, Number, SelectInput, Tag, Text, TextInput } from "components/common"
import { IconTextDate, StatusTag, TitleDescription } from "components/custom"
import { CLIENT_DETAIL_PATH, CONTRACT_PATH, EDIT_BOOKING_FORM } from "constants/app-paths"
import dayjs from "dayjs"
import UploadDraggerS3 from "features/shared/components/uploadDraggerS3/uploadDraggerS3"
import { classifications } from "features/shared/data"
import { t } from "i18next"
import { Link } from "react-router-dom"
import { convertCamelCaseToReadable, convertToReadableString, localisedValue } from "utils"
import getPrivilege from "utils/privileges/privileges"
import Ageing from "../todos/customComponents/ageing"
import AllocatedTo from "../todos/customComponents/allocatedTo"
import Interest from "../todos/customComponents/interest"
import UpdatedAt from "../todos/customComponents/updatedAt"
import UnitID from "../todos/customComponents/unitID"
import InvoiceId from "../todos/customComponents/invoiceId"

export const fractionizeTime = seconds => {
  let durationInSeconds = dayjs.duration(seconds, "seconds")
  let fractionizeTime = []

  let days = durationInSeconds.days()
  let hours = durationInSeconds.hours()

  fractionizeTime.push(`${days} Days`)
  fractionizeTime.push(`${hours} Hours`)

  return fractionizeTime.join(" ")
}

export const componentMap = {
  attachment: UploadDraggerS3,
  text: TextInput,
  numeric: InputNumber,
  date: DatePicker,
  dropdown: SelectInput,
}
export const additionalProps = setLoading => ({
  text: { placeholder: "Enter your text", maxLength: 126 },
  numeric: { min: 0, style: { width: "100%" }, placeholder: t("Please enter a numeric value") },
  attachment: {
    allowedFileSize: 10,
    isDuplicateFileAllowed: false,
    maxCount: 1,
    allowedFiles:
      "application/pdf, image/png, image/jpeg, image/pjpeg, .jfif, application/vnd.ms-excel.sheet.macroenabled.12, application/vnd.ms-excel, application/msword, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/zip, application/x-zip-compressed, application/x-rar-compressed, application/vnd.rar, .zip, .rar",
    showButton: true,
    path: ({ fileName }) => `/workflows/writePresignedUrl?fileName=${fileName}`,
    setLoading: setLoading,
    uploadDescription: t(
      "Must be in JPG, JPEG, XLSX, DOCX, PNG, JFIF, PDF, ZIP or RAR file format up till 10 MB Upload all the evidence screenshots, chat conversation or emails that help in your claim."
    ),
  },
  date: { showTime: false },
  dropdown: {
    getOptionValue: item => item?.key,
    getOptionLabel: item => item?.value,
    mode: "multiple",
    getPopupContainer: false,
    placeholder: t("Please select an option"),
  },
})

export const dynamicListingComponentMapper = (type, dataObject, privileges) => {
  switch (type) {
    case "assignee":
    case "requested_by":
      return <AllocatedTo data={dataObject} />
    case "shared_with":
      return (
        <AllocatedTo
          data={{
            ...dataObject[0],
            designationShort: dataObject?.[0]?.designation?.short,
            designationShortAR: dataObject?.[0]?.designation?.shortAR,
            regionName: dataObject?.[0]?.city?.name,
            regionNameAR: dataObject?.[0]?.city?.nameAR,
          }}
        />
      )
    case "updated_at":
      return <UpdatedAt data={dataObject} />
    case "ageing":
      return <Ageing data={dataObject} />
    case "lead_id":
      return (
        <IconTextDate
          message={classifications()[dataObject?.classification?.id]?.name}
          iconProps={{ color: classifications()[dataObject?.classification?.id]?.style.color, hasBackground: true }}
          icon="MdLocalFireDepartment"
          title={dataObject?.id}
          date={dataObject?.createdAt}
          customTooltip={t(`Lead Added on`) + ": "}
          linkInfo={
            getPrivilege("can_view_client_details", privileges)
              ? CLIENT_DETAIL_PATH.replace(":clientId", dataObject?.clientId) +
                `?activeTab=workflows&requestStatus=${dataObject?.activeStatus}`
              : false
          }
        />
      )
    case "client_name":
      return (
        <>
          <Flex vertical gap={"6px"}>
            {getPrivilege("can_view_client_details", privileges) ? (
              <Link to={CLIENT_DETAIL_PATH.replace(":clientId", dataObject?.id)} className="text-link">
                {localisedValue({ key: "name", object: dataObject })}
              </Link>
            ) : (
              <div>{localisedValue({ key: "name", object: dataObject })}</div>
            )}
            <FLagWithText
              title={localisedValue({ key: "name", object: dataObject?.country })}
              countryCode={dataObject?.country?.locCode}
              text={localisedValue({ key: "name", object: dataObject?.country })}
            />
          </Flex>
        </>
      )
    case "client_id":
      return (
        <>
          <Flex vertical gap={"6px"}>
            {getPrivilege("can_view_client_details", privileges) ? (
              <Link to={CLIENT_DETAIL_PATH.replace(":clientId", dataObject?.id)} className="text-link">
                {dataObject?.id}
              </Link>
            ) : (
              <div>{dataObject?.id}</div>
            )}
            <DateFormat date={dataObject?.createdAt} />
          </Flex>
        </>
      )
    case "lead_allocation":
      return (
        <AllocatedTo
          data={{
            designationShort: localisedValue({ object: dataObject?.designation, key: "short" }),
            regionName: localisedValue({ object: dataObject?.city, key: "name" }),
            name: localisedValue({ object: dataObject, key: "name" }),
            id: dataObject?.id,
          }}
        />
      )

    case "interest":
      return <Interest data={dataObject} privileges={privileges} />
    case "contract_id":
      return (
        <>
          {getPrivilege("can_view_contracts_module", privileges) ? (
            <Link target="_blank" to={CONTRACT_PATH.concat("?contractId=", dataObject)} className="text-link">
              {dataObject}
            </Link>
          ) : (
            <div>{dataObject}</div>
          )}
        </>
      )
    case "unit_id":
      return <UnitID data={dataObject} privileges={privileges} />
    case "contract_status":
    case "booking_form_status":
    case "invoice_status":
      return <StatusTag data={convertToReadableString(dataObject)}>{convertToReadableString(dataObject)}</StatusTag>
    case "booking_form_number":
      return (
        <>
          {getPrivilege("can_update_accounts_document", privileges) ? (
            <Link
              target="_blank"
              to={EDIT_BOOKING_FORM.replace(":bookingFormId", dataObject?.id)}
              className="text-link"
            >
              {dataObject?.bookingFormNumber}
            </Link>
          ) : (
            <div>{dataObject?.bookingFormNumber}</div>
          )}
        </>
      )
    case "invoice_id":
      return <InvoiceId data={dataObject} privileges={privileges} />
    case "department":
    case "source":
      return (
        <>
          <TitleDescription
            title={
              <Row>
                <Tag
                  type="light"
                  children={localisedValue({
                    key: "clientReferralType",
                    object: dataObject,
                  })?.[0]?.toUpperCase()}
                  style={{ cursor: "pointer" }}
                  tooltipText={
                    localisedValue({
                      key: "clientReferralType",
                      object: dataObject,
                    })?.[0]?.toUpperCase() +
                    localisedValue({ key: "clientReferralType", object: dataObject })?.substring(1)
                  }
                />
                <div>{localisedValue({ key: "name", object: dataObject?.source })}</div>
              </Row>
            }
            description={localisedValue({ key: "name", object: dataObject?.platform })}
          />
        </>
      )
    case "developer":
    case "total_tasks":
      return <Text>{dataObject}</Text>
    case "last_activity_ageing":
      return <Ageing data={{ stageAgeing: dataObject }} />
    case "sale_type":
    case "lead_type":
      return <Text>{convertCamelCaseToReadable(dataObject)}</Text>
    case "payment_amount":
    case "refund_amount":
    case "invoice_amount":
    case "revenue_recognized":
      return <Number tooltip isCurrency isStringOnly value={dataObject} />

    default:
      return <Text>{dataObject?.id}</Text>
  }
}
